import React, { lazy, Suspense, useRef, useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { Redirect, Router } from '@reach/router';
import { useSelector, useDispatch } from 'react-redux';
import { useIsOnline } from 'react-use-is-online';
import { Toaster } from 'react-hot-toast';
import { setLayerSettings } from '#/user/slice';
import '@reach/skip-nav/styles.css';
import PageLayout from '#/components/PageLayout';
import TripToolsContainer from '#/containers/TripToolsContainer';
import RouteListContainer from '#/containers/RouteListContainer';
import TripToolsLayout from '#/components/TripToolsLayout';
import SystemWideServiceAlertsContainer from '#/containers/SystemWideServiceAlertsContainer';
import StopDetailContainer from '#/containers/StopDetailContainer';
import RouteServiceAlertsContainer from '#/containers/RouteServiceAlertsContainer';
import ServiceAlertsListContainer from '#/containers/ServiceAlertsListContainer';
import GeneralOmniboxContainer from '#/containers/GeneralOmniboxContainer';
import TripPlanContainer from '#/containers/TripPlanContainer';
import TripRequestContainer from '#/containers/TripRequestContainer';
import ROUTES from '#/constants/routes';
import LocationDetailContainer from '#/containers/LocationDetailContainer';
import TripModeSelectorContainer from '#/containers/TripModeSelectorContainer';
import TripDateTimeSelectorContainer from '#/containers/TripDateTimeSelectorContainer';
import VehicleDetailContainer from '#/containers/VehicleDetailContainer';
import RouteDetailContainer from '#/containers/RouteDetailContainer';
import CurrentLocationManager from '#/shared/components/CurrentLocationManager';
import HistoryManager from '#/shared/components/HistoryManager';
import RiderFeedbackContainer from '#/containers/RiderFeedbackContainer';
import PayContainer from '#/containers/PayContainer';
import NavBar from '#/shared/components/NavBar';
import CookiesWarningContainer from '#/containers/CookiesWarningContainer';
import useGtmDataLayer from '#/shared/hooks/useGtmDataLayer';
import closeMapPopup from '#/shared/utils/close-map-popup';
import useBasePath from '#/shared/hooks/useBasePath';
import { ToasterA11yWrapper } from '#/components/SoftToast';
import 'leaflet-edgebuffer';
import { FEATURE_FLAGS } from '#/shared/constants';

const { MAPLIBRE_MODE } = FEATURE_FLAGS;

const MapContainer = lazy(() =>
  MAPLIBRE_MODE
    ? import('#/containers/RMGLMapContainer')
    : import('#/containers/MapContainer')
);

const StyledSidePanelRouter = styled(Router).attrs(() => ({
  // the `Router` component has a default value of -1 for `tabIndex`
  // which was taking the focus away from the omnibox's input on mount
  tabIndex: null,
}))`
  display: ${props => (props.minimized ? 'none' : 'flex')};
  flex: 1 0 auto;
  flex-direction: column;
  width: 100%;
`;

function IndexPage() {
  useGtmDataLayer();

  const dispatch = useDispatch();
  const { isLayerSettingsOpened } = useSelector(s => s.user);
  const toggleLayerSettings = () => {
    dispatch(setLayerSettings(!isLayerSettingsOpened));
  };
  const closeLayerDialog = () => dispatch(setLayerSettings(false));
  const contentPanelRef = useRef();

  const basePath = useBasePath();
  // React-leaflet will not reload the maptiles unless a full re-mount occurs
  // a key is added to the MapContainer to manually re-mount after network re-connect
  const [updateKey, setUpdateKey] = useReducer(x => x + 1, 0);
  const { isOnline } = useIsOnline();
  const prevIsOnline = useRef(isOnline);

  useEffect(() => {
    if (prevIsOnline.current !== isOnline && isOnline === true) {
      setUpdateKey();
    }
    prevIsOnline.current = isOnline;
  }, [isOnline]);

  useEffect(() => {
    if (window.history.scrollRestoration)
      // disable browser feature that restores the previous scroll
      // position on a page when it is reloaded
      window.history.scrollRestoration = 'manual';

    // undo any scrolling when navigating away from this page, without
    // this pages like the menu don't always load at the top on e.g. iOS
    return () => window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <HistoryManager />
      <PageLayout pageIsInert={isLayerSettingsOpened}>
        <CurrentLocationManager />
        <CookiesWarningContainer />
        <Router primary={false} basepath={basePath} onClick={closeLayerDialog}>
          <SystemWideServiceAlertsContainer path="/*" />
        </Router>
        <Router primary={false} basepath={basePath}>
          <NavBar path="/*" closeMapPopup={closeMapPopup} />
        </Router>
        <TripToolsLayout
          contentPanelRef={contentPanelRef}
          closeMapPopup={closeMapPopup}
          underlay={
            <Suspense fallback={<div>Loading map...</div>}>
              {MAPLIBRE_MODE ? (
                <MapContainer
                  key={updateKey}
                  contentPanelRef={contentPanelRef}
                  toggleLayerSettings={toggleLayerSettings}
                />
              ) : (
                <MapContainer
                  key={updateKey}
                  isLayerSettingsOpened={isLayerSettingsOpened}
                  contentPanelRef={contentPanelRef}
                  toggleLayerSettings={toggleLayerSettings}
                />
              )}
            </Suspense>
          }
          overlay={<Router primary={false} basepath={basePath} />}
          overlayOpen={isLayerSettingsOpened}
          side={
            typeof window !== 'undefined' ? (
              <StyledSidePanelRouter basepath={basePath}>
                <TripToolsContainer path={ROUTES.home.path} />
                <ServiceAlertsListContainer path={ROUTES.alerts.path} />
                <RouteServiceAlertsContainer path={ROUTES.alertsById.path} />
                <StopDetailContainer path={ROUTES.stopById.path} />
                <StopDetailContainer path={ROUTES.stopByIdAndRoute.path} />
                <LocationDetailContainer path={ROUTES.location.path} />
                <VehicleDetailContainer path={ROUTES.vehicle.path} />
                <VehicleDetailContainer
                  path={ROUTES.vehicleWithSelectedStop.path}
                />
                <TripPlanContainer path={ROUTES.trip.path} />
                <TripRequestContainer path={ROUTES.planner.path} />
                <TripModeSelectorContainer
                  path={ROUTES.plannerModeSettings.path}
                />
                <TripDateTimeSelectorContainer
                  path={ROUTES.plannerTimeSettings.path}
                />
                <RouteListContainer path={ROUTES.routes.path} />
                <RouteDetailContainer path={ROUTES.routeById.path} />
                <RiderFeedbackContainer path={ROUTES.riderFeedback.path} />
                <PayContainer path={ROUTES.pay.path} />
                <GeneralOmniboxContainer path={ROUTES.search.path} />

                {/* if route is not matched redirect to base */}
                <Redirect
                  from="*"
                  // `Redirect` doesn't honor the Router's `basepath`
                  to={`${basePath}${ROUTES.home.path}`}
                  default
                  noThrow
                />
              </StyledSidePanelRouter>
            ) : null
          }
        />
        <ToasterA11yWrapper>
          <Toaster position="bottom-center" />
        </ToasterA11yWrapper>
      </PageLayout>
    </>
  );
}

export default IndexPage;
